import { Button } from '@cian/ui-kit/button';
import { equals } from 'ramda';
import * as React from 'react';

import * as styles from './AdvancedFiltersContainer.css';
import { getAvailableFilters } from './helpers';
import { LOCAL_STORAGE_IS_DESKTOP_FILTERS_WERE_APPLIED } from '../../../../../../shared/common/hooks/useUxFbPoll/constants';
import {
  getLocalStorageFiltersWereAppliedTwoTimes,
  setLocalStorageFiltersApplied,
} from '../../../../../../shared/common/hooks/useUxFbPoll/localStorageUtils';
import { IJsonQuery } from '../../../../../api-models/common/json_query';
import { AdvancedFilters } from '../../../components/AdvancedFiltersComponent';
import { ADVANCED_FILTERS } from '../../../constants/advancedFilters';
import { isAdvancedFiltersSelected, getUncommonProperties } from '../../../utils/advancedFilters';
import { useFiltersContext } from '../../../utils/useFiltersContext';

export const AdvancedFiltersContainer: React.FC = () => {
  const { jsonQuery, onChange, onApply, onClear, onApplyClick, onAdvancedFiltersOpen, features } = useFiltersContext();
  const isAnythingSelected = isAdvancedFiltersSelected(jsonQuery);
  const [isOpen, setIsOpen] = React.useState(false);
  const [jsonQueryBackup, setJsonQueryBackup] = React.useState<IJsonQuery>();
  const [isAwaitingJsonQueryUpdate, setIsAwaitingJsonQueryUpdate] = React.useState<boolean>(false);
  const [isJsonQueryUpdated, setIsJsonQueryUpdated] = React.useState<boolean>(false);

  const availableFilters = getAvailableFilters(jsonQuery);

  const isLocalStorageFiltersWereAppliedTwoTimes = getLocalStorageFiltersWereAppliedTwoTimes(
    LOCAL_STORAGE_IS_DESKTOP_FILTERS_WERE_APPLIED,
  );

  const handleClick = React.useCallback(() => {
    setIsOpen(true);

    if (onAdvancedFiltersOpen) {
      onAdvancedFiltersOpen();
    }
  }, [onAdvancedFiltersOpen]);

  const handleClose = React.useCallback(() => {
    if (jsonQueryBackup) {
      onChange({ action: 'setJsonQuery', arguments: [jsonQueryBackup] });
    }

    if (isJsonQueryUpdated || !equals(jsonQuery, jsonQueryBackup)) {
      onApply();
    }

    setIsOpen(false);
  }, [isJsonQueryUpdated, jsonQuery, jsonQueryBackup, onApply, onChange]);

  const handleClear = React.useCallback(() => {
    onChange({ action: 'resetTerms', arguments: [[...getUncommonProperties(jsonQuery), 'for_day']] });

    setIsAwaitingJsonQueryUpdate(true);
    setIsJsonQueryUpdated(true);

    if (onClear) {
      onClear();
    }
  }, [jsonQuery, onChange, onClear]);

  const handleShowClick = React.useCallback(() => {
    if (onApplyClick) {
      onApplyClick();
    }

    onApply();
    setIsOpen(false);

    //Если фильтр был применен 2 раза, сохраняем значеине в LocalStorage
    if (!isLocalStorageFiltersWereAppliedTwoTimes) {
      setLocalStorageFiltersApplied(LOCAL_STORAGE_IS_DESKTOP_FILTERS_WERE_APPLIED);
    }
  }, [onApply, onApplyClick, setLocalStorageFiltersApplied, isLocalStorageFiltersWereAppliedTwoTimes]);

  React.useEffect(() => {
    if (isOpen) {
      setJsonQueryBackup(jsonQuery);
    } else {
      setJsonQueryBackup(undefined);
      setIsAwaitingJsonQueryUpdate(false);
      setIsJsonQueryUpdated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  React.useEffect(() => {
    if (isAwaitingJsonQueryUpdate) {
      setJsonQueryBackup(jsonQuery);
      setIsAwaitingJsonQueryUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonQuery]);

  return (
    <>
      <div className={styles['button']} data-name="FilterAdvanced">
        <Button theme="stroke_secondary" size="XS" onClick={handleClick}>
          Ещё фильтры
        </Button>
        {isAnythingSelected ? <div className={styles['badge']} /> : null}
      </div>
      <AdvancedFilters open={isOpen} onClearClick={handleClear} onShowClick={handleShowClick} onClose={handleClose}>
        {availableFilters.map(filterKey => {
          const { availability, component: Component } = ADVANCED_FILTERS[filterKey];

          if (availability && !availability(jsonQuery, features)) {
            return null;
          }

          return <Component key={`filter_${filterKey}`} />;
        })}
      </AdvancedFilters>
    </>
  );
};
